import FeatureGates from '@atlaskit/feature-gate-js-client';

interface IdleDetectorOptions {
	/** Callback to invoke when idle timeout elapses. Receives an {@link AbortSignal}
	 *  that will be aborted when the idle detector is stopped, reset, or destroyed,
	 *  so the callback can cancel any in-flight work (e.g. a network request). */
	onIdle: (options: { signal: AbortSignal }) => void;
}

/** Default timeout in milliseconds. */
export const DEFAULT_TIMEOUT_MS = 2000;

function isNumber(value: unknown): value is number {
	return typeof value === 'number';
}

function getExperimentValue(key: string, defaultValue: number): number {
	// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
	return FeatureGates.getExperimentValue(
		'platform_editor_ai_quick_prompt_iw_cc',
		key,
		defaultValue,
		{
			typeGuard: isNumber,
		},
	);
}

function getTimeoutMs(): number {
	return getExperimentValue('timeout', DEFAULT_TIMEOUT_MS);
}

export class IdleDetector {
	private timeoutMs: number;
	private timerId: ReturnType<typeof setTimeout> | null = null;
	private onIdle: (options: { signal: AbortSignal }) => void;
	private destroyed = false;
	private abortController: AbortController | null = null;

	constructor(options: IdleDetectorOptions) {
		this.timeoutMs = getTimeoutMs();
		this.onIdle = options.onIdle;
	}

	/** Abort any in-flight work from the previous idle callback. */
	private abortInflight(): void {
		if (this.abortController) {
			this.abortController.abort();
			this.abortController = null;
		}
	}

	stop(): void {
		if (this.timerId !== null) {
			clearTimeout(this.timerId);
			this.timerId = null;
		}
		this.abortInflight();
	}

	/** Reset the idle timer */
	reset(): void {
		if (this.destroyed) {
			return;
		}

		this.stop();

		this.timerId = setTimeout(() => {
			this.timerId = null;
			this.abortController = new AbortController();
			this.onIdle({ signal: this.abortController.signal });
		}, this.timeoutMs);
	}

	/** Cancel pending timer and clean up */
	destroy(): void {
		this.stop();
		this.destroyed = true;
	}
}
